// src/components/NotificationBell.js
// Bell icon + unread badge for the app nav. Self-contained: subscribes to the
// recipient's single-doc unread counter (cheap real-time badge), owns the open
// state, and renders the NotificationCenter panel.
//
// Opening the panel resets the badge counter ("seen" — the bell stops nagging);
// per-item "new" highlight is handled inside NotificationCenter ("read").
//
// Rendered by AppNav in BOTH the desktop top bar and the mobile top bar. Only one
// nav is visible at a time (CSS media queries), so only the visible bell is ever
// interacted with. Returns null when there's no signed-in user (e.g. demo mode).

import React, { useState, useEffect } from 'react';
import { subscribeToUnreadCount, markAllSeen } from '../services/commentNotifications';
import NotificationCenter from './NotificationCenter';

function NotificationBell({ userId }) {
    const [count, setCount] = useState(0);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!userId) { setCount(0); return undefined; }
        const unsub = subscribeToUnreadCount(userId, setCount);
        return () => { try { unsub(); } catch (_) { /* noop */ } };
    }, [userId]);

    if (!userId) return null;

    const handleOpen = () => {
        setOpen(true);
        // Reset the badge on open — fire-and-forget; the counter onSnapshot will
        // reflect 0 shortly. Per-item highlights remain until each is tapped.
        if (count > 0) markAllSeen(userId);
    };

    const badge = count > 99 ? '99+' : (count > 9 ? '9+' : String(count));

    return (
        <>
            <button
                onClick={handleOpen}
                aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
                style={{
                    position: 'relative', background: 'none', border: 'none', cursor: 'pointer',
                    width: 40, height: 40, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 0, WebkitTapHighlightColor: 'transparent',
                    transition: 'background 0.15s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.05)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
            >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                    stroke="var(--mom-charcoal)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {count > 0 && (
                    <span style={{
                        position: 'absolute', top: 2, right: 2,
                        minWidth: 16, height: 16, padding: '0 4px', boxSizing: 'border-box',
                        borderRadius: 8, background: 'var(--mom-primary)', color: '#fff',
                        fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700,
                        lineHeight: '16px', textAlign: 'center',
                        boxShadow: '0 0 0 2px var(--mom-cream)',
                    }}>{badge}</span>
                )}
            </button>
            <NotificationCenter userId={userId} open={open} onClose={() => setOpen(false)} />
        </>
    );
}

export default NotificationBell;
